import React, { useState } from 'react';

import { Modal, PrimaryButton } from '../../components';
import { apiBaseUrl } from '../../util/api';

import css from './EventSubmissionModal.module.css';

const INITIAL_FIELDS = {
  eventName: '',
  eventType: '',
  startDate: '',
  endDate: '',
  city: '',
  province: '',
  venue: '',
  organizer: '',
  websiteUrl: '',
  description: '',
  notes: '',
  submitterName: '',
  submitterEmail: '',
  // Honeypot: hidden from real users, bots tend to fill every field in.
  company: '',
};

const REQUIRED_FIELDS = [
  'eventName',
  'eventType',
  'startDate',
  'city',
  'province',
  'websiteUrl',
  'submitterName',
  'submitterEmail',
];

const EventSubmissionModal = ({ isOpen, onClose, onManageDisableScrolling }) => {
  const [fields, setFields] = useState(INITIAL_FIELDS);
  const [accurateConfirmation, setAccurateConfirmation] = useState(false);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const updateField = field => event => {
    setFields(current => ({ ...current, [field]: event.target.value }));
    setError(null);
  };

  const handleClose = () => {
    onClose();
    // Reset state after the close animation/unmount so the form is fresh next time it opens.
    setFields(INITIAL_FIELDS);
    setAccurateConfirmation(false);
    setError(null);
    setSubmitted(false);
  };

  const submit = event => {
    event.preventDefault();

    const missingField = REQUIRED_FIELDS.find(field => !fields[field].trim());
    if (missingField) {
      setError('Please fill in all required fields.');
      return;
    }
    if (!accurateConfirmation) {
      setError('Please confirm that the information submitted is accurate.');
      return;
    }

    setSubmitting(true);
    setError(null);

    fetch(`${apiBaseUrl()}/api/event-submissions`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ...fields, accurateConfirmation }),
    })
      .then(async response => {
        const data = await response.json();
        if (!response.ok) throw new Error(data.error || 'Unable to send submission.');
        setSubmitted(true);
      })
      .catch(requestError => setError(requestError.message))
      .finally(() => setSubmitting(false));
  };

  return (
    <Modal
      id="EventSubmissionModal"
      isOpen={isOpen}
      onClose={handleClose}
      onManageDisableScrolling={onManageDisableScrolling}
    >
      <div className={css.root}>
        {submitted ? (
          <div className={css.successState}>
            <h2 className={css.title}>Thanks for the tip 💜</h2>
            <p className={css.description}>
              Every submission is reviewed before it appears on PageHop.
            </p>
          </div>
        ) : (
          <>
            <h2 className={css.title}>Submit a bookish event</h2>
            <p className={css.description}>
              Know about a bookish event Canadian readers should have on their radar? Send it our
              way. We review submissions before adding them to PageHop.
            </p>

            <form onSubmit={submit}>
              {/* Honeypot field: hidden from real users via CSS, left blank by them. */}
              <div className={css.honeypot} aria-hidden="true">
                <label htmlFor="event-submission-company">Company</label>
                <input
                  id="event-submission-company"
                  name="company"
                  type="text"
                  tabIndex="-1"
                  autoComplete="off"
                  value={fields.company}
                  onChange={updateField('company')}
                />
              </div>

              <div className={css.fieldGrid}>
                <label className={css.label} htmlFor="event-submission-name">
                  Event name*
                  <input
                    id="event-submission-name"
                    className={css.input}
                    value={fields.eventName}
                    onChange={updateField('eventName')}
                  />
                </label>

                <label className={css.label} htmlFor="event-submission-type">
                  Event type*
                  <input
                    id="event-submission-type"
                    className={css.input}
                    placeholder="Book fair, author signing, festival…"
                    value={fields.eventType}
                    onChange={updateField('eventType')}
                  />
                </label>

                <label className={css.label} htmlFor="event-submission-start-date">
                  Start date*
                  <input
                    id="event-submission-start-date"
                    className={css.input}
                    type="date"
                    value={fields.startDate}
                    onChange={updateField('startDate')}
                  />
                </label>

                <label className={css.label} htmlFor="event-submission-end-date">
                  End date
                  <input
                    id="event-submission-end-date"
                    className={css.input}
                    type="date"
                    value={fields.endDate}
                    onChange={updateField('endDate')}
                  />
                </label>

                <label className={css.label} htmlFor="event-submission-city">
                  City*
                  <input
                    id="event-submission-city"
                    className={css.input}
                    value={fields.city}
                    onChange={updateField('city')}
                  />
                </label>

                <label className={css.label} htmlFor="event-submission-province">
                  Province / territory*
                  <input
                    id="event-submission-province"
                    className={css.input}
                    value={fields.province}
                    onChange={updateField('province')}
                  />
                </label>

                <label className={css.label} htmlFor="event-submission-venue">
                  Venue
                  <input
                    id="event-submission-venue"
                    className={css.input}
                    value={fields.venue}
                    onChange={updateField('venue')}
                  />
                </label>

                <label className={css.label} htmlFor="event-submission-organizer">
                  Organizer / host
                  <input
                    id="event-submission-organizer"
                    className={css.input}
                    value={fields.organizer}
                    onChange={updateField('organizer')}
                  />
                </label>

                <label className={css.label} htmlFor="event-submission-website">
                  Event website or ticket link*
                  <input
                    id="event-submission-website"
                    className={css.input}
                    type="url"
                    placeholder="https://"
                    value={fields.websiteUrl}
                    onChange={updateField('websiteUrl')}
                  />
                </label>
              </div>

              <label className={css.label} htmlFor="event-submission-description">
                Event description
                <textarea
                  id="event-submission-description"
                  className={css.textarea}
                  value={fields.description}
                  onChange={updateField('description')}
                />
              </label>

              <label className={css.label} htmlFor="event-submission-notes">
                Notes for PageHop
                <textarea
                  id="event-submission-notes"
                  className={css.textarea}
                  value={fields.notes}
                  onChange={updateField('notes')}
                />
              </label>

              <div className={css.fieldGrid}>
                <label className={css.label} htmlFor="event-submission-submitter-name">
                  Your name*
                  <input
                    id="event-submission-submitter-name"
                    className={css.input}
                    value={fields.submitterName}
                    onChange={updateField('submitterName')}
                  />
                </label>

                <label className={css.label} htmlFor="event-submission-submitter-email">
                  Your email*
                  <input
                    id="event-submission-submitter-email"
                    className={css.input}
                    type="email"
                    value={fields.submitterEmail}
                    onChange={updateField('submitterEmail')}
                  />
                </label>
              </div>

              <label className={css.confirmationLabel} htmlFor="event-submission-confirm">
                <input
                  id="event-submission-confirm"
                  type="checkbox"
                  checked={accurateConfirmation}
                  onChange={event => {
                    setAccurateConfirmation(event.target.checked);
                    setError(null);
                  }}
                />
                I confirm that the information submitted is accurate to the best of my knowledge.
              </label>

              {error ? <p className={css.error}>{error}</p> : null}

              <div className={css.actions}>
                <button className={css.cancel} type="button" onClick={handleClose}>
                  Cancel
                </button>
                <PrimaryButton type="submit" inProgress={submitting} disabled={submitting}>
                  Send to PageHop
                </PrimaryButton>
              </div>
            </form>
          </>
        )}
      </div>
    </Modal>
  );
};

export default EventSubmissionModal;
